import React from "react";
import { NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LOGO } from "../../lib/constants";
import { LayoutDashboard, ClipboardList, CalendarDays, Users, CreditCard, CalendarX, Cake, PartyPopper, Settings, LogOut } from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Resumen", end: true },
  { to: "/admin/pedidos", icon: ClipboardList, label: "Pedidos" },
  { to: "/admin/calendario", icon: CalendarDays, label: "Calendario" },
  { to: "/admin/clientes", icon: Users, label: "Clientes" },
  { to: "/admin/pagos", icon: CreditCard, label: "Pagos" },
  { to: "/admin/disponibilidad", icon: CalendarX, label: "Disponibilidad" },
  { to: "/admin/catalogo", icon: Cake, label: "Catálogo" },
  { to: "/admin/servicios", icon: PartyPopper, label: "Servicios" },
  { to: "/admin/configuracion", icon: Settings, label: "Configuración" },
];

// mobile bottom nav shows first 5 most-used
const MOBILE = [NAV[0], NAV[1], NAV[2], NAV[6], NAV[3]];

export const DashboardLayout = ({ children, title }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-crema flex w-full overflow-x-hidden">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-60 xl:w-72 bg-blancoCalido border-r border-beige fixed inset-y-0">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-beige">
          <img src={LOGO} alt="" className="w-9 h-9 rounded-full" />
          <span className="font-serif text-xl text-vino">Kelifa</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition-all ${isActive ? "bg-vino text-crema" : "text-cafe/70 hover:bg-beige/60"}`}>
              <n.icon className="w-5 h-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="logout-btn" className="m-3 flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm text-vino hover:bg-beige/60">
          <LogOut className="w-5 h-5" /> Cerrar sesión
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 w-full lg:ml-60 xl:ml-72 pb-[calc(5.75rem+env(safe-area-inset-bottom))] lg:pb-8 overflow-x-hidden">
        <header className="sticky top-0 z-30 bg-crema/95 backdrop-blur-md border-b border-beige px-4 sm:px-8 min-h-16 flex items-center justify-between">
          <div className="min-w-0">
            <p className="lg:hidden text-[11px] uppercase tracking-[0.2em] text-cafe/45 leading-none pt-3">Admin Kelifa</p>
            <h1 className="font-serif text-[1.7rem] sm:text-2xl text-vino leading-tight py-3 truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:block text-sm text-cafe/60">{user?.name}</span>
            <button onClick={doLogout} className="lg:hidden text-vino" data-testid="logout-mobile"><LogOut className="w-5 h-5" /></button>
          </div>
        </header>
        <div className="lg:hidden sticky top-16 z-20 bg-crema/95 backdrop-blur-md border-b border-beige/70">
          <div className="flex gap-2 overflow-x-auto overscroll-x-contain px-4 py-2 no-scrollbar max-w-full">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] border transition-all whitespace-nowrap ${isActive ? "bg-vino text-crema border-vino shadow-pastel" : "bg-blancoCalido text-cafe/65 border-beige"}`}>
                <n.icon className="w-3.5 h-3.5 shrink-0" /> <span>{n.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
        <main className="w-full max-w-[1600px] mx-auto px-4 py-4 sm:px-6 lg:px-8 xl:px-10 sm:py-8 overflow-x-hidden">{children}</main>
      </div>

      {/* Bottom nav mobile */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-blancoCalido border-t border-beige rounded-t-3xl shadow-[0_-8px_30px_rgba(118,27,56,0.06)] flex justify-around pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] px-1">
        {MOBILE.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mnav-${n.label}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl text-[10px] ${isActive ? "text-vino" : "text-cafe/50"}`}>
            <n.icon className="w-5 h-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center bg-crema text-vino">Cargando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};
